// Names and colours copied from the iOS appearance screen
// (mobile/app/appearance.tsx) and palettes (mobile/src/theme/index.ts).
// The web bundle stays self-contained: these values are inlined, not imported
// from the mobile package.

export const IOS_FONT_SCALES = [
  { key: 'small', label: 'Small' },
  { key: 'medium', label: 'Default' },
  { key: 'large', label: 'Large' },
  { key: 'xlarge', label: 'X-Large' },
];

export const IOS_APPEARANCE = [
  {
    key: 'blue',
    name: 'Classic Blue',
    description: 'White background with blue accents',
    isDark: false,
    bg: '#F1F5F9',
    surface: '#FFFFFF',
    text: '#0F172A',
    primary: '#0F52BA',
    primaryDark: '#0A3D8F',
    primaryLight: '#3B82F6',
    primaryTint: '#E7EEFB',
    hero: ['#0B1E3F', '#12294F'],
    card: '#0B1E3F',
    cardAlt: '#12294F',
    heroLight: false,
  },
  {
    key: 'dark',
    name: 'Classic Dark',
    description: 'Cool near-black with a real blue header',
    isDark: true,
    bg: '#0B0F17',
    surface: '#151B26',
    text: '#F1F5F9',
    primary: '#4A84F4',
    primaryDark: '#2F6BE0',
    primaryLight: '#7FA8F8',
    primaryTint: '#17233D',
    hero: ['#1B3A6B', '#0E244A'],
    card: '#1B3A6B',
    cardAlt: '#0E244A',
    heroLight: false,
  },
  {
    key: 'blush',
    name: 'Blush Pink',
    description: 'Soft blush & rose-gold, airy and light',
    isDark: false,
    bg: '#FBEFF3',
    surface: '#FFFFFF',
    text: '#3A2530',
    primary: '#D6607F',
    primaryDark: '#B44768',
    primaryLight: '#E88BA6',
    primaryTint: '#FBE1EA',
    hero: ['#F7C6D6', '#E9B4D2', '#F4D2C2'],
    card: '#D6607F',
    cardAlt: '#A85C9C',
    heroLight: true,
  },
  {
    key: 'indigo',
    name: 'Midnight Indigo',
    description: 'Premium deep-indigo dark mode',
    isDark: true,
    bg: '#0E1230',
    surface: '#171C42',
    text: '#EAECFF',
    primary: '#7C8CFF',
    primaryDark: '#5A6BE8',
    primaryLight: '#A0ACFF',
    primaryTint: '#1E2352',
    hero: ['#3B3F8F', '#1E2160'],
    card: '#3B3F8F',
    cardAlt: '#1E2160',
    heroLight: false,
  },
  {
    key: 'warm',
    name: 'Warm Reading',
    description: 'Low-glare sepia, easy on the eyes',
    isDark: false,
    bg: '#F4ECD8',
    surface: '#FBF6E9',
    text: '#3B2F22',
    primary: '#9A5B34',
    primaryDark: '#7A4526',
    primaryLight: '#B87A4E',
    primaryTint: '#EFE0CB',
    hero: ['#5A4632', '#3B2F22'],
    card: '#5A4632',
    cardAlt: '#3B2F22',
    heroLight: false,
  },
  {
    key: 'wicked',
    name: 'Wicked Purple',
    description: 'Emerald Nebula, violet & emerald',
    isDark: true,
    bg: '#140A2E',
    surface: '#241147',
    text: '#F2E9FF',
    primary: '#9D4EDD',
    primaryDark: '#7B2FD6',
    primaryLight: '#C08CF0',
    primaryTint: '#241147',
    hero: ['#7B2FD6', '#5B2E8C', '#2E7D4F', '#35D07A'],
    card: '#5B2E8C',
    cardAlt: '#35D07A',
    heroLight: false,
  },
  {
    key: 'sunset-light',
    name: 'Sunset Pop Light',
    description: 'Bright magenta & violet on white',
    isDark: false,
    bg: '#FDF0F7',
    surface: '#FFFFFF',
    text: '#2A0E22',
    primary: '#FF3D8B',
    primaryDark: '#D6206E',
    primaryLight: '#FF6FA8',
    primaryTint: '#FFE0EE',
    hero: ['#C238E0', '#7A3BF5'],
    card: '#C238E0',
    cardAlt: '#7A3BF5',
    heroLight: false,
  },
  {
    key: 'sunset-dark',
    name: 'Sunset Pop Dark',
    description: 'Deep plum with neon magenta',
    isDark: true,
    bg: '#160A1E',
    surface: '#241230',
    text: '#FCE9F4',
    primary: '#FF3D8B',
    primaryDark: '#D6206E',
    primaryLight: '#FF6FA8',
    primaryTint: '#2E1330',
    hero: ['#C238E0', '#7A3BF5'],
    card: '#C238E0',
    cardAlt: '#7A3BF5',
    heroLight: false,
  },
];

export function iosTheme(key) {
  return IOS_APPEARANCE.find((row) => row.key === key) || IOS_APPEARANCE[0];
}

// Solid card colour from the iOS palette (`colors.hero` / `colors.heroAlt`).
// The contact card and My Card use this in every photo state. The header
// gradient above is a separate band.
export function iosCardSurface(key) {
  const row = iosTheme(key);
  return {
    background: row.card,
    alt: row.cardAlt,
    foreground: '#FFFFFF',
    muted: 'rgba(255,255,255,0.82)',
    link: '#BBD3FF',
    scrim: 'rgba(255,255,255,0.12)',
  };
}

export function iosHeroCss(key) {
  const stops = iosTheme(key).hero;
  const parts = stops.map((color, index) => {
    const at = stops.length === 1 ? 0 : Math.round((index / (stops.length - 1)) * 100);
    return `${color} ${at}%`;
  });
  return `linear-gradient(135deg, ${parts.join(', ')})`;
}

function channel(c) {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

function luminance(hex) {
  const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!match) return null;
  const n = parseInt(match[1], 16);
  return (
    0.2126 * channel((n >> 16) & 255)
    + 0.7152 * channel((n >> 8) & 255)
    + 0.0722 * channel(n & 255)
  );
}

export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  if (la == null || lb == null) return 0;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

const SECTION_HEADER_MIN_CONTRAST = 4.5;

// Same rule as mobile/src/theme/sectionHeader.ts: bold theme colour that
// stays readable on the page background.
export function sectionHeaderColor(colors, isDark) {
  const candidates = [colors.primary, isDark ? colors.primaryLight : colors.primaryDark];
  for (const candidate of candidates) {
    if (contrastRatio(candidate, colors.bg) >= SECTION_HEADER_MIN_CONTRAST) return candidate;
  }
  return colors.text;
}
